import React, { useRef, useEffect, useState } from 'react';
import { useAnimationFrame } from '../hooks/useAnimationFrame';

const ArrangementMatrix = ({
    sections = [],
    events = [],
    onAddSection,
    onRemoveSection,
    onUpdateSection,
    onReorderSections, // New Prop
    getTransportTime,
    totalDuration = 0,
    isPlaying = false
}) => {
    const playheadRef = useRef(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const progressBarRef = useRef(null);

    useAnimationFrame(() => {
        if (progressBarRef.current && getTransportTime) {
            const t = getTransportTime();
            // Handle edge case where totalDuration might be 0
            const pct = totalDuration > 0 ? Math.min((t / totalDuration) * 100, 100) : 0;
            progressBarRef.current.style.width = `${pct}%`;
        }
    }, isPlaying);

    // --- HELPER: Reorder Sections ---
    const moveSection = (fromIndex, toIndex) => {
        if (fromIndex === null || fromIndex === toIndex) return;
        const newSections = [...sections];
        const [movedSection] = newSections.splice(fromIndex, 1);
        newSections.splice(toIndex, 0, movedSection);
        if (onReorderSections) {
            onReorderSections(newSections);
        }
    };

    // Drag & Drop Handlers
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault(); // Necessary to allow dropping
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        moveSection(draggedIndex, dropIndex);
        setDraggedIndex(null);
    };

    const handleTouchEnd = (e, index) => {
        // Safety: Check if touch exists
        if (!e.changedTouches || e.changedTouches.length === 0) return;

        const touch = e.changedTouches[0];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);

        // Safety: Null check for target (finger off-screen)
        if (!target) {
            setDraggedIndex(null);
            return;
        }

        const dropTarget = target.closest('[data-index]');
        if (dropTarget) {
            const dropIndex = parseInt(dropTarget.getAttribute('data-index'), 10);
            if (!isNaN(dropIndex)) {
                moveSection(index, dropIndex);
            }
        }
        setDraggedIndex(null);
    };

    const SECTION_TYPES = [
        { label: 'Intro', value: 'intro' },
        { label: 'Verse', value: 'verse' },
        { label: 'Pre-Chorus', value: 'pre_chorus' },
        { label: 'Chorus', value: 'chorus' },
        { label: 'Bridge', value: 'bridge' },
        { label: 'Solo', value: 'solo' },
        { label: 'Interlude', value: 'interlude' },
        { label: 'Outro', value: 'outro' }
    ];

    return (
    <div className="timeline-container panel" style={{ width: '100%', marginTop: '20px', position: 'relative' }}>

      {/* 1. Playback Progress Bar (Top - simplified context) */}
      <div style={{ width: '100%', height: '4px', background: '#111', marginBottom: '10px', position: 'relative', borderRadius: '2px', overflow: 'hidden' }}>
          <div ref={progressBarRef} style={{ width: '0%', height: '100%', background: 'var(--color-primary)' }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: '0', fontSize: '0.9rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Arrangement Editor
        </h3>

        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '70%' }}>
            {SECTION_TYPES.map(type => (
                <button
                    key={type.value}
                    onClick={() => onAddSection(type.value, 8)}
                    className="btn-small"
                    style={{
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        background: '#333',
                        border: '1px solid #444',
                        color: '#eee',
                        cursor: 'pointer'
                    }}
                >
                    + {type.label}
                </button>
            ))}
        </div>
      </div>

      {/* 2. Arrangement Editor (Horizontal Strip) */}
      <div
        style={{
            width: '100%',
            overflowX: 'auto', // Horizontal scrolling
            backgroundColor: '#050608',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'inset 0 0 10px #000',
            padding: '10px',
            boxSizing: 'border-box' // Fix for bleeding
        }}
      >
        <div
            style={{
                display: 'flex',
                flexDirection: 'row', // Horizontal
                gap: '8px',
                minWidth: '100%',
                boxSizing: 'border-box'
            }}
        >
            {!Array.isArray(sections) || sections.length === 0 ? (
                <div style={{ color: '#444', padding: '20px', width: '100%', textAlign: 'center' }}>No Sections. Add one to start.</div>
            ) : (
                sections.map((section, index) => {
                    let borderColor = 'var(--color-border)';
                    if (section.type === 'chorus') borderColor = 'var(--neon-blue)';
                    if (section.type === 'verse') borderColor = 'var(--color-primary)';
                    if (section.type === 'bridge') borderColor = '#FFC107';
                    if (section.type === 'intro' || section.type === 'outro') borderColor = '#9E9E9E';
                    if (section.type === 'solo') borderColor = '#E91E63';

                    return (
                        <div
                            key={index}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onTouchStart={() => setDraggedIndex(index)}
                            onTouchEnd={(e) => handleTouchEnd(e, index)}
                            data-index={index}
                            style={{
                                minWidth: '130px', // Fixed width for horizontal items
                                width: '130px',
                                boxSizing: 'border-box',
                                background: '#1a1a1a',
                                border: `1px solid ${borderColor}`,
                                borderTop: `4px solid ${borderColor}`, // Border on top for horizontal stack
                                borderLeft: `1px solid ${borderColor}`,
                                borderRadius: 'var(--radius-md)',
                                padding: '5px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                cursor: 'grab',
                                opacity: draggedIndex === index ? 0.5 : 1
                            }}
                        >
                            {/* Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ color: '#666', fontSize: '0.8rem' }}>#{index + 1}</div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onRemoveSection(index); }}
                                    style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: '1rem', padding: 0 }}
                                    title="Remove Section"
                                >×</button>
                            </div>

                            {/* Section Name */}
                            <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '0.9rem', textTransform: 'capitalize', textAlign: 'center' }}>
                                {(section.section || section.type).replace('_', ' ')}
                            </div>

                            {/* Controls Container */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', position: 'relative', zIndex: 1 }}>
                                {/* Length */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <label style={{ fontSize: '0.7rem', color: '#aaa' }}>Len:</label>
                                    <select
                                        value={section.bars}
                                        onChange={(e) => onUpdateSection(index, { bars: parseInt(e.target.value) })}
                                        style={{ background: '#333', color: '#fff', border: 'none', fontSize: '0.8rem', padding: '2px 4px', borderRadius: '2px', width: '80px' }}
                                    >
                                        {[4, 8, 16, 32].map(b => <option key={b} value={b}>{b} Bars</option>)}
                                    </select>
                                </div>

                                {/* Time Sig */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <label style={{ fontSize: '0.7rem', color: '#aaa' }}>Sig:</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        <input
                                            type="number"
                                            min="1" max="32"
                                            value={section.timeSignature?.numerator || 4}
                                            onChange={(e) => onUpdateSection(index, {
                                                timeSignature: { ...(section.timeSignature || { denominator: 4 }), numerator: parseInt(e.target.value, 10) }
                                            })}
                                            style={{ width: '30px', background: '#333', color: '#fff', border: 'none', fontSize: '0.8rem', textAlign: 'center', borderRadius: '2px' }}
                                        />
                                        <span style={{ color: '#666' }}>/</span>
                                        <select
                                            value={section.timeSignature?.denominator || 4}
                                            onChange={(e) => onUpdateSection(index, {
                                                timeSignature: { ...(section.timeSignature || { numerator: 4 }), denominator: parseInt(e.target.value, 10) }
                                            })}
                                            style={{ width: '40px', background: '#333', color: '#fff', border: 'none', fontSize: '0.8rem', borderRadius: '2px' }}
                                        >
                                            {[2, 4, 8, 16].map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
      </div>
    </div>
  );
};

export default ArrangementMatrix;
